import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { MY_CLUB_PAGE } from 'constants/pages';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import BounceLogoSplashShadow from 'svg/BounceLogoSplashShadow';
import { ButtonLink } from 'components/Button';
import Modal, { useModal } from 'components/modals/Modal';

export const SHOW_CLUB_DASHBOARD_PROMPT_STORAGE_KEY = 'showClubDashboardPrompt';
// Dispatched by FormLogin on login flows that don't navigate afterwards, since no
// route/user change would otherwise re-trigger the flag check.
export const CLUB_DASHBOARD_PROMPT_CHECK_EVENT = 'clubDashboardPromptCheck';

export default function ModalClubDashboardPrompt() {
  const router = useRouter();
  const { user } = useGetCurrentUser();
  const { isOpen, openModal, closeModal } = useModal();

  useEffect(() => {
    const checkAndOpen = () => {
      if (!user?.id) {
        return;
      }

      if (sessionStorage.getItem(SHOW_CLUB_DASHBOARD_PROMPT_STORAGE_KEY) !== 'true') {
        return;
      }

      sessionStorage.removeItem(SHOW_CLUB_DASHBOARD_PROMPT_STORAGE_KEY);

      if (user.adminVenueId && !router.asPath.startsWith(MY_CLUB_PAGE)) {
        openModal();
      }
    };

    checkAndOpen();
    window.addEventListener(CLUB_DASHBOARD_PROMPT_CHECK_EVENT, checkAndOpen);
    return () => {
      window.removeEventListener(CLUB_DASHBOARD_PROMPT_CHECK_EVENT, checkAndOpen);
    };
  }, [user?.id, router.asPath]);

  // Mounted globally in _app, so close on navigation or it stays open over the next page.
  useEffect(() => {
    router.events.on('routeChangeStart', closeModal);
    return () => {
      router.events.off('routeChangeStart', closeModal);
    };
  }, []);

  return (
    <Modal isOpen={isOpen} handleClose={closeModal} classNameMaxWidth="max-w-[37.5rem]">
      <div className="flex flex-col p-2">
        <h1 className="flex shrink-0 items-center justify-center space-x-4 px-4 pt-12 text-center sm:px-[6.25rem] lg:pt-10">
          <span className="text-2xl font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            <BounceLogoSplashShadow className="h-[6.5rem]" aria-label="Bounce Logo" />
          </span>
        </h1>
        <h2 className="typography-product-display mt-1 text-pretty px-2 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Welcome back!
        </h2>
        <h3 className="typography-product-body mt-ds-md text-pretty px-2 text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          You're an admin of a club on Bounce. Head to manage your club profile, programming and
          coaches.
        </h3>
        <ButtonLink
          href={MY_CLUB_PAGE}
          variant="primary"
          size="lg"
          isInline
          className="mx-auto my-8"
        >
          Go to club dashboard
        </ButtonLink>
      </div>
    </Modal>
  );
}
